import React, { useState, useRef, useEffect, useCallback } from "react";
import ContactActions from "../ContactActions/ContactActions";
import "./BackgroundVideo.css";

interface BackGroundVideoProps {
  headingLines: string[];
}

const hasLimitedResources = () => {
  try {
    const connection = (navigator as any).connection;
    const deviceMemory = (navigator as any).deviceMemory;
    return (
      connection?.saveData ||
      connection?.effectiveType?.includes('2g') ||
      (deviceMemory && deviceMemory < 4)
    );
  } catch {
    return false;
  }
};

const BackGroundVideo: React.FC<BackGroundVideoProps> = ({ headingLines }) => {
  const [showFallback, setShowFallback] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // Lazily import the video only when the section is near the viewport
  const loadVideo = useCallback(() => {
    if (hasLimitedResources()) {
      setShowFallback(true);
      return;
    }
    import('../../../assets/video/background.mp4').then((mod) => {
      setVideoSrc(mod.default);
    });
  }, []);

  useEffect(() => {
    // On mobile with limited resources, skip entirely
    if (hasLimitedResources()) {
      setShowFallback(true);
      return;
    }

    const el = sectionRef.current;
    if (!el) { loadVideo(); return; }

    // Start loading the video when the hero section is within 200px of viewport
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { loadVideo(); io.disconnect(); } },
      { rootMargin: '200px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadVideo]);

  // Once the video src is set, attempt autoplay
  useEffect(() => {
    if (!videoSrc) return;
    const videoElement = videoRef.current;
    if (!videoElement) return;

    videoElement.play()
      .catch(() => setShowFallback(true));
  }, [videoSrc]);

  return (
    <main>
      <section ref={sectionRef} className="heroSection">
        {!showFallback && videoSrc && (
          <video
            ref={videoRef}
            className="heroVideo"
            autoPlay
            loop
            muted
            playsInline
            poster="/fallbackhero.webp"
            preload="metadata"
            style={{ objectFit: 'cover' }}
            onError={() => setShowFallback(true)}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        )}

        {!showFallback && !videoSrc && (
          <div
            className="heroVideo fallback-bg"
            style={{ backgroundImage: 'url(/fallbackhero.webp)' }}
          />
        )}

        {showFallback && (
          <div 
            className="heroVideo fallback-bg"
            style={{ backgroundImage: 'url(/fallbackhero.webp)' }}
          />
        )}

        <div className="heroContent">
          <h1 className="heroTitle text-center text-white">
            {headingLines.map((line, index) => (
              <span key={index} style={{ display: 'block' }}>
                {line}
              </span>
            ))}
          </h1>
          <ContactActions />
        </div>
      </section>
    </main>
  );
};

export default BackGroundVideo;